import { useState } from 'react';
import { Link } from 'react-router';
import type { GatewayStatus, TenantView } from '@teachpartner/contracts';
import { describeError } from '../api/client';
import { useServices } from '../app-context';
import { ErrorBanner, ExposureBadge, HealthIndicator, StatusBadge } from '../components/badges';
import {
  EMPTY_TENANT,
  TenantForm,
  toTenantFields,
  type TenantFormValues,
} from '../components/TenantForm';
import { VersionCell } from '../components/version';
import { useAsync } from '../hooks/use-async';

function subscriptionLabel(t: TenantView): string {
  const { plan, endsOn } = t.subscription;
  if (!plan && !endsOn) return '—';
  return [plan ?? 'no plan', endsOn ? `until ${endsOn}` : null].filter(Boolean).join(' · ');
}

function TenantTable({ tenants }: { tenants: TenantView[] }) {
  if (tenants.length === 0) return <p>No tenants yet.</p>;
  return (
    <table>
      <thead>
        <tr>
          <th>Code</th>
          <th>Name</th>
          <th>Hostname</th>
          <th>Exposure</th>
          <th>Status</th>
          <th>Subscription</th>
          <th>Health</th>
          <th>Version</th>
          <th>Enrolled</th>
        </tr>
      </thead>
      <tbody>
        {tenants.map((t) => (
          <tr key={t.code}>
            <td>
              <Link to={`/tenants/${t.code}`}>{t.code}</Link>
            </td>
            <td>{t.name}</td>
            <td>{t.canonicalHostname}</td>
            <td>
              <ExposureBadge mode={t.exposureMode} />
            </td>
            <td>
              <StatusBadge status={t.status} />
            </td>
            <td>{subscriptionLabel(t)}</td>
            <td>
              <HealthIndicator health={t.health} />
            </td>
            <td>
              <VersionCell tenant={t} />
            </td>
            <td>{t.enrolledAt ? 'yes' : 'no'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function LatestRelease({ latest }: { latest: string | null }) {
  return (
    <p className="muted">
      {latest
        ? `Latest release: ${latest}. Schools running an older one are marked Outdated.`
        : 'Latest release not configured (TP_LATEST_RELEASE): versions are not compared.'}
    </p>
  );
}

function GatewayPanel({ gateway, onReload }: { gateway: GatewayStatus; onReload(): void }) {
  const last = gateway.lastSync;
  return (
    <section aria-label="Gateway">
      <h2>Gateway</h2>
      <p>
        {gateway.routes.length} SNI route(s).{' '}
        {last
          ? `Last sync ${last.at}: ${last.ok ? 'ok' : `failed — ${last.error ?? 'unknown error'}`}`
          : 'Not synced since start.'}
      </p>
      <button type="button" onClick={onReload}>
        Re-render and reload gateway
      </button>
    </section>
  );
}

export function TenantsPage() {
  const { api } = useServices();
  const list = useAsync(() => api.listTenants(), [api]);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const run = async (action: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      setNotice(await action());
      list.reload();
    } catch (e) {
      setError(describeError(e));
    } finally {
      setBusy(false);
    }
  };

  const create = (values: TenantFormValues) =>
    run(async () => {
      const result = await api.createTenant({
        code: values.code.trim(),
        bootstrapAdminEmail: values.bootstrapAdminEmail.trim(),
        ...toTenantFields(values),
      });
      setCreating(false);
      return [
        `Created ${result.tenant.code}. Gateway ${result.gateway.ok ? 'updated' : 'NOT updated'}.`,
        ...result.warnings.map((w) => w.message),
      ].join(' ');
    });

  const reload = () =>
    run(async () => {
      const result = await api.reloadGateway();
      return result.ok ? 'Gateway reloaded.' : `Gateway reload failed: ${result.error ?? ''}`;
    });

  return (
    <div>
      <h1>Tenants</h1>
      <ErrorBanner message={error ?? list.error} />
      {notice && <div className="banner banner-info">{notice}</div>}
      <button type="button" onClick={() => setCreating((c) => !c)}>
        {creating ? 'Cancel' : 'New tenant'}
      </button>
      {creating && (
        <TenantForm initial={EMPTY_TENANT} mode="create" busy={busy} onSubmit={create} />
      )}
      {list.loading && !list.data ? <p>Loading…</p> : null}
      {list.data && <LatestRelease latest={list.data.latestRelease} />}
      {list.data && <TenantTable tenants={list.data.tenants} />}
      {list.data && <GatewayPanel gateway={list.data.gateway} onReload={reload} />}
    </div>
  );
}
